// Volumes 3D conformes au relief pour les polygones. `fill-extrusion` de Mapbox
// n'échantillonne le terrain qu'AUX SOMMETS du polygone (pas de subdivision interne
// pour les extrusions normales — vérifié dans le moteur), donc un grand polygone
// devient un plateau. Ici on construit le volume nous-mêmes : on triangule le
// polygone (earcut), on SUBDIVISE jusqu'à des arêtes courtes, puis on cale chaque
// sommet à terrain+plancher / terrain+plafond → la surface suit vraiment le relief.
//
// Une seule couche WebGL custom héberge tous les volumes (three.js, même plomberie
// que les rideaux). Origine bakée en double précision, géométrie en delta Mercator.
import * as THREE from 'three'
import earcut from 'earcut'
import mapboxgl from 'mapbox-gl'
import { getRoot } from '/@/root'

const LAYER_ID = '__volumes__'
const DEFAULT_COLOR = '#2D8CF0'
const STEP_M = 30 // taille de maille cible (m) — compromis conformité / nombre de triangles

interface VolumeEntry {
  mesh: THREE.Mesh
  material: THREE.MeshBasicMaterial
  geometry: THREE.BufferGeometry
}

let scene: THREE.Scene | null = null
let camera: THREE.Camera | null = null
let renderer: THREE.WebGLRenderer | null = null
const volumes = new Map<string, VolumeEntry>()

function getMap (): mapboxgl.Map | null {
  return (getRoot() as any)?.$map || null
}

function ensureLayer (map: mapboxgl.Map): void {
  if (!scene) {
    scene = new THREE.Scene()
    camera = new THREE.Camera()
  }
  if (map.getLayer(LAYER_ID)) return
  const layer: any = {
    id: LAYER_ID,
    type: 'custom',
    renderingMode: '3d',
    onAdd (_map: mapboxgl.Map, gl: WebGLRenderingContext) {
      renderer = new THREE.WebGLRenderer({ canvas: _map.getCanvas(), context: gl as any, antialias: false })
      renderer.autoClear = false
    },
    render (_gl: WebGLRenderingContext, matrix: number[]) {
      if (!renderer || !scene || !camera) return
      if (volumes.size === 0) return
      camera.projectionMatrix = new THREE.Matrix4().fromArray(matrix as number[])
      renderer.resetState()
      renderer.render(scene, camera)
      map.triggerRepaint()
    },
  }
  map.addLayer(layer)
}

function haversine (a: number[], b: number[]): number {
  const R = 6371000
  const dLat = (b[1] - a[1]) * Math.PI / 180
  const dLng = (b[0] - a[0]) * Math.PI / 180
  const x = Math.sin(dLat / 2) ** 2 +
    Math.cos(a[1] * Math.PI / 180) * Math.cos(b[1] * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
}

export interface VolumeOpts {
  floor: number
  floorRef: 'agl' | 'msl'
  ceiling: number
  ceilingRef: 'agl' | 'msl'
  color?: string
  opacity?: number
}

function terrainAt (map: mapboxgl.Map, lng: number, lat: number): number {
  // Terrain exagéré : le volume se cale sur le sol rendu (cohérent avec le reste).
  return ((map as any).queryTerrainElevation({ lng, lat }, { exaggerated: true }) ?? 0)
}

// Triangule les anneaux (extérieur + trous) puis subdivise jusqu'à des arêtes < STEP_M,
// en partageant les milieux d'arête entre triangles voisins. Retourne sommets (lng,lat)
// et triangles (indices). Borné pour éviter l'explosion sur de très grands polygones.
function tessellate (rings: number[][][]): { verts: number[][], tris: number[] } {
  const verts: number[][] = []
  const flat: number[] = []
  const holeIndices: number[] = []
  rings.forEach((ring, ri) => {
    const r = ring.slice()
    if (r.length > 1 && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1]) r.pop()
    if (ri > 0) holeIndices.push(verts.length)
    for (const p of r) { verts.push([p[0], p[1]]); flat.push(p[0], p[1]) }
  })
  let tris = earcut(flat, holeIndices.length ? holeIndices : undefined, 2)

  const midCache = new Map<string, number>()
  const midpoint = (a: number, b: number): number => {
    const key = a < b ? a + '_' + b : b + '_' + a
    const c = midCache.get(key)
    if (c != null) return c
    const idx = verts.length
    verts.push([(verts[a][0] + verts[b][0]) / 2, (verts[a][1] + verts[b][1]) / 2])
    midCache.set(key, idx)
    return idx
  }
  const longEnough = (a: number, b: number) => haversine(verts[a], verts[b]) > STEP_M

  for (let pass = 0; pass < 7 && verts.length < 60000; pass++) {
    let changed = false
    const next: number[] = []
    for (let i = 0; i < tris.length; i += 3) {
      const a = tris[i]; const b = tris[i + 1]; const c = tris[i + 2]
      if (longEnough(a, b) || longEnough(b, c) || longEnough(c, a)) {
        const ab = midpoint(a, b); const bc = midpoint(b, c); const ca = midpoint(c, a)
        next.push(a, ab, ca, ab, b, bc, ca, bc, c, ab, bc, ca)
        changed = true
      } else {
        next.push(a, b, c)
      }
    }
    tris = next
    if (!changed) break
  }
  return { verts, tris }
}

function buildVolume (map: mapboxgl.Map, rings: number[][][], o: VolumeOpts): VolumeEntry | null {
  const outer = rings[0]
  if (!outer || outer.length < 3) return null
  if (!(o.ceiling > o.floor || o.ceilingRef !== o.floorRef)) return null

  const altOf = (terr: number, value: number, ref: 'agl' | 'msl') => ref === 'msl' ? value : terr + value

  const { verts, tris } = tessellate(rings)
  const terr = verts.map(v => terrainAt(map, v[0], v[1]))
  const floorAlt = verts.map((_, i) => altOf(terr[i], o.floor, o.floorRef))
  const ceilAlt = verts.map((_, i) => altOf(terr[i], o.ceiling, o.ceilingRef))

  const o0 = mapboxgl.MercatorCoordinate.fromLngLat([verts[0][0], verts[0][1]], floorAlt[0])
  const merc = (lng: number, lat: number, alt: number) => {
    const m = mapboxgl.MercatorCoordinate.fromLngLat([lng, lat], alt)
    return [m.x - o0.x, m.y - o0.y, m.z - o0.z]
  }

  const positions: number[] = []
  const indices: number[] = []
  const pushVert = (lng: number, lat: number, alt: number): number => {
    const p = merc(lng, lat, alt)
    const idx = positions.length / 3
    positions.push(p[0], p[1], p[2])
    return idx
  }

  // Cap plancher + cap plafond (deux jeux de sommets, mêmes triangles).
  const floorBase = positions.length / 3
  for (let i = 0; i < verts.length; i++) pushVert(verts[i][0], verts[i][1], floorAlt[i])
  const ceilBase = positions.length / 3
  for (let i = 0; i < verts.length; i++) pushVert(verts[i][0], verts[i][1], ceilAlt[i])
  for (let i = 0; i < tris.length; i += 3) {
    indices.push(floorBase + tris[i], floorBase + tris[i + 1], floorBase + tris[i + 2])
    indices.push(ceilBase + tris[i], ceilBase + tris[i + 2], ceilBase + tris[i + 1]) // winding inversé (cosmétique)
  }

  // Parois : le long de chaque anneau densifié, quads plancher→plafond.
  for (const ring of rings) {
    const dense: number[][] = []
    for (let i = 0; i < ring.length - 1; i++) {
      const a = ring[i]; const b = ring[i + 1]
      const n = Math.max(1, Math.min(2000, Math.round(Math.max(haversine(a, b), 0.001) / STEP_M)))
      for (let k = 0; k < n; k++) {
        const t = k / n
        dense.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
      }
    }
    dense.push(ring[ring.length - 1])
    for (let i = 0; i < dense.length - 1; i++) {
      const p0 = dense[i]; const p1 = dense[i + 1]
      const t0 = terrainAt(map, p0[0], p0[1]); const t1 = terrainAt(map, p1[0], p1[1])
      const b0 = pushVert(p0[0], p0[1], altOf(t0, o.floor, o.floorRef))
      const u0 = pushVert(p0[0], p0[1], altOf(t0, o.ceiling, o.ceilingRef))
      const b1 = pushVert(p1[0], p1[1], altOf(t1, o.floor, o.floorRef))
      const u1 = pushVert(p1[0], p1[1], altOf(t1, o.ceiling, o.ceilingRef))
      indices.push(b0, b1, u0, u0, b1, u1)
    }
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setIndex(indices)
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(o.color || DEFAULT_COLOR),
    transparent: true,
    opacity: o.opacity ?? 0.35,
    side: THREE.DoubleSide,
    depthWrite: false,
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.position.set(o0.x, o0.y, o0.z)
  return { mesh, material, geometry }
}

/** Crée / remplace le volume d'un polygone. `rings` = [[ [lng,lat], … ], trous…]. */
export function setVolume (id: string, rings: number[][][], opts: VolumeOpts): void {
  const map = getMap()
  if (!map || !rings?.length) return
  removeVolume(id)
  ensureLayer(map)
  if (!scene) return
  const entry = buildVolume(map, rings, opts)
  if (!entry) return
  scene.add(entry.mesh)
  volumes.set(id, entry)
  map.triggerRepaint()
}

export function setVolumeColor (id: string, color?: string): void {
  const entry = volumes.get(id)
  if (!entry) return
  entry.material.color.set(color || DEFAULT_COLOR)
  getMap()?.triggerRepaint()
}

export function removeVolume (id: string): void {
  const entry = volumes.get(id)
  if (!entry) return
  scene?.remove(entry.mesh)
  entry.geometry.dispose()
  entry.material.dispose()
  volumes.delete(id)
  getMap()?.triggerRepaint()
}
